import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { SiteHeader } from '../components/SiteHeader';
import { SEO } from '../components/SEO';

/**
 * Public Services page. One automated service (dormant accounts filing)
 * and three concierge services (confirmation statement, company closure,
 * ad-hoc filings) where the customer reaches us via the contact form and
 * we file manually + quote a price by email.
 *
 * Service cards link out to either `/search` (the automated wizard) or
 * `/contact?service=<slug>` which preselects the right subject in the
 * contact form.
 */
export function Services() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="Our Services — Dormant Accounts Filing"
        description="Dormant accounts filing from £20, plus concierge confirmation statements, company closures, and ad-hoc Companies House filings."
        path="/services"
      />
      <SiteHeader />

      {/* ── Hero ───────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-6 pt-12 pb-8 text-center sm:pt-16">
        <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-100">
          Our services
        </span>
        <h1 className="mt-4 text-4xl font-semibold tracking-tight sm:text-5xl">
          Companies House filing – done for you
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
          From dormant accounts you file in five minutes to the trickier
          changes we handle for you by hand. UK-based, fixed prices on
          common filings, quotes by email on the rest.
        </p>
      </section>

      {/* ── Service cards ──────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="grid gap-6 sm:grid-cols-2">
          {/* – Dormant Accounts (automated, headline service) – */}
          <article className="relative flex flex-col rounded-2xl bg-gradient-to-br from-brand-50 to-white p-6 ring-1 ring-brand-100 sm:col-span-2 sm:p-8">
            <span className="absolute right-4 top-4 rounded-full bg-brand-700 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
              Automated
            </span>
            <div className="flex items-start gap-4 pr-24">
              <div className="rounded-lg bg-brand-100 p-3 text-brand-700"><IconDoc /></div>
              <div className="min-w-0">
                <h2 className="text-2xl font-semibold tracking-tight">Dormant Accounts Filing</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Form AA02. The most common filing for any UK company that hasn't traded.
                </p>
              </div>
            </div>

            <ul className="mt-6 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
              <Tick>Files directly with Companies House in minutes</Tick>
              <Tick>Same-day confirmation email + receipt</Tick>
              <Tick>FRS-102 compliant iXBRL accounts</Tick>
              <Tick>Annual reminder so you never miss the deadline</Tick>
            </ul>

            <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="text-3xl font-semibold text-slate-900">£20</div>
                <div className="text-xs text-slate-500">Fixed price · No subscriptions · No add-ons</div>
              </div>
              <Link to="/search" className="btn-primary">Start filing →</Link>
            </div>
          </article>

          {/* – Confirmation Statement (own page, structured form) – */}
          <ServiceCard
            badge="Fixed fee"
            icon={<IconCheck />}
            title="Confirmation Statement"
            tagline="Annual Companies House record refresh – formerly known as the annual return."
            bullets={[
              "Updates registered office, SIC codes, and shareholder details",
              "We complete and file the CS01 on your behalf",
              "Same-day filing once you confirm details with us",
            ]}
            pricingNote="£90 fixed fee – filed within 1 working day."
            ctaLabel="File now →"
            ctaTo="/confirmation-statement"
          />

          {/* – Company Closure (own page, structured enquiry form) – */}
          <ServiceCard
            badge="By enquiry"
            icon={<IconClose />}
            title="Company Closure"
            tagline="Voluntary strike-off (DS01) – close a company you no longer need."
            bullets={[
              "We prepare and submit the DS01 application to Companies House",
              "Guidance on closure conditions (no recent trading, no creditors, etc.)",
              "Notification to HMRC + dissolution timeline tracking",
            ]}
            pricingNote="Pricing confirmed by email after a quick review of your company."
            ctaLabel="Start enquiry →"
            ctaTo="/closure"
          />

          {/* – Ad-hoc (catch-all) – */}
          <ServiceCard
            badge="By enquiry"
            icon={<IconAdhoc />}
            title="Ad-hoc Filings"
            tagline="One-off changes to your company record. We file the right form for you."
            bullets={[
              "Change of registered office",
              "Director appointment or resignation",
              "Director details change",
              "Share allotments, name changes, and other ad-hoc filings",
            ]}
            pricingNote="Pricing confirmed by email – most filings are fixed-fee."
            ctaLabel="Tell us what you need →"
            ctaTo="/contact?service=adhoc"
            wide
          />
        </div>

        {/* ── Why us ───────────────────────────────────────────────── */}
        <div className="mt-12 rounded-2xl bg-slate-50 p-8 ring-1 ring-slate-200 sm:p-10">
          <div className="grid items-center gap-8 sm:grid-cols-3">
            <div className="text-center">
              <div className="text-3xl font-semibold text-brand-700">£20</div>
              <div className="mt-1 text-sm text-slate-600">Flat-fee dormant filing – no hidden extras</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-semibold text-brand-700">Same-day</div>
              <div className="mt-1 text-sm text-slate-600">Most filings reach Companies House the same working day</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-semibold text-brand-700">🇬🇧</div>
              <div className="mt-1 text-sm text-slate-600">UK-based team – real people, not chatbots</div>
            </div>
          </div>
        </div>

        {/* ── Closing CTA ──────────────────────────────────────────── */}
        <div className="mt-12 text-center">
          <p className="text-sm text-slate-600">Not sure which filing you need?</p>
          <Link to="/contact" className="btn-ghost mt-3 inline-flex">
            Ask us – we'll point you the right way
          </Link>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto grid max-w-6xl gap-6 px-6 py-10 text-sm text-slate-600 sm:grid-cols-3">
          <div>
            <Logo />
            <p className="mt-3 text-xs leading-relaxed">
              UK Companies House filing service. We get the paperwork right so you can focus on your business.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Services</div>
            <ul className="mt-3 space-y-2">
              <li><Link to="/search" className="hover:text-slate-900">Dormant accounts (£20)</Link></li>
              <li><Link to="/confirmation-statement" className="hover:text-slate-900">Confirmation statement (£90)</Link></li>
              <li><Link to="/closure" className="hover:text-slate-900">Company closure</Link></li>
              <li><Link to="/contact?service=adhoc" className="hover:text-slate-900">Ad-hoc filings</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Company</div>
            <ul className="mt-3 space-y-2">
              <li><Link to="/blog" className="hover:text-slate-900">Blog</Link></li>
              <li><Link to="/contact" className="hover:text-slate-900">Contact</Link></li>
              <li><Link to="/privacy" className="hover:text-slate-900">Privacy</Link></li>
              <li><Link to="/terms" className="hover:text-slate-900">Terms</Link></li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ── Card component ─────────────────────────────────────────────────── */
function ServiceCard({
  badge,
  icon,
  title,
  tagline,
  bullets,
  pricingNote,
  ctaLabel,
  ctaTo,
  wide,
}: {
  badge: string;
  icon: React.ReactNode;
  title: string;
  tagline: string;
  bullets: string[];
  pricingNote: string;
  ctaLabel: string;
  ctaTo: string;
  wide?: boolean;
}) {
  return (
    <article
      className={`relative flex flex-col rounded-2xl bg-white p-6 ring-1 ring-slate-200 transition hover:ring-brand-200 ${
        wide ? 'sm:col-span-2' : ''
      }`}
    >
      <span className="absolute right-4 top-4 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
        {badge}
      </span>
      <div className="flex items-start gap-4 pr-24">
        <div className="rounded-lg bg-slate-100 p-3 text-slate-700">{icon}</div>
        <div className="min-w-0">
          <h3 className="text-xl font-semibold tracking-tight">{title}</h3>
          <p className="mt-1 text-sm text-slate-600">{tagline}</p>
        </div>
      </div>
      <ul className="mt-5 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
        {bullets.map((b) => (
          <Tick key={b}>{b}</Tick>
        ))}
      </ul>
      {/* Footer block pinned to the bottom of the card so the buttons
          line up horizontally across cards in the same row, regardless
          of how many bullets each card has. The pricing note gets a
          min-height so a short note in one card and a wrapped one in
          another don't push their buttons to different vertical
          positions. */}
      <div className="mt-auto pt-6">
        <div className="flex items-end justify-between gap-3 border-t border-slate-100 pt-4">
          <div className="min-h-[2.5em] flex-1 text-xs text-slate-500">{pricingNote}</div>
          <Link to={ctaTo} className="btn-primary flex-none">
            {ctaLabel}
          </Link>
        </div>
      </div>
    </article>
  );
}

function Tick({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full bg-brand-50 text-brand-700">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
      <span>{children}</span>
    </li>
  );
}

/* ── Inline icons ───────────────────────────────────────────────────── */
function IconDoc() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="15" y2="17" />
    </svg>
  );
}
function IconCheck() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 11 12 14 22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}
function IconClose() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 21h18" />
      <path d="M5 21V7l8-4v18" />
      <path d="M19 21V11l-6-4" />
      <line x1="9" y1="9" x2="9" y2="9" />
      <line x1="9" y1="12" x2="9" y2="12" />
      <line x1="9" y1="15" x2="9" y2="15" />
      <line x1="9" y1="18" x2="9" y2="18" />
    </svg>
  );
}
function IconAdhoc() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
